import { apiRequestWithAutoRefresh } from '../utils/apiUtils'
import type {
  ResolvedLevelMap,
  LevelMapOverview,
  LevelTierInput,
  LevelTier,
  LevelLabelMap
} from '../types/levelMap'

// ==================== 夥伴端 ====================

/**
 * 取得自己部門的升等地圖。
 * 夥伴只看得到自己部門的——地圖含各級時薪，等同薪資結構，不開放互看。
 */
export const getMyLevelMap = async (): Promise<ResolvedLevelMap> => {
  const result = await apiRequestWithAutoRefresh('/level-maps/mine', { method: 'GET' })
  return result.data
}

/**
 * 全部門的職等名稱／顏色對照表。
 * 用於顯示**別人**的職等徽章——別人的職等名稱取決於他的部門，不是登入者的部門。
 */
export const getLevelLabels = async (): Promise<LevelLabelMap> => {
  const result = await apiRequestWithAutoRefresh('/level-maps/labels', { method: 'GET' })
  return result.data
}

// ==================== 管理端 ====================

export const getLevelMapOverview = async (): Promise<LevelMapOverview> => {
  const result = await apiRequestWithAutoRefresh('/level-maps/admin/maps', { method: 'GET' })
  return result.data
}

/** 取某部門「含回退」的完整解析結果；department 傳 null 代表預設地圖 */
export const getLevelMapByDepartment = async (
  department: string | null
): Promise<ResolvedLevelMap> => {
  const key = department ? encodeURIComponent(department) : '__default__'
  const result = await apiRequestWithAutoRefresh(`/level-maps/admin/maps/by-department/${key}`, {
    method: 'GET'
  })
  return result.data
}

/** 取某張地圖「含回退」的完整解析結果（編輯共用範本用） */
export const getResolvedLevelMapById = async (id: string): Promise<ResolvedLevelMap> => {
  const result = await apiRequestWithAutoRefresh(`/level-maps/admin/maps/${id}/resolved`, {
    method: 'GET'
  })
  return result.data
}

/**
 * 取某位夥伴實際適用的地圖（個人指定 → 部門 → 預設），與後端審核、升等用的是同一套解析。
 * 管理員調整別人的職等、審核升等時用這支，不要自己依部門猜。
 */
export const getLevelMapForUser = async (userId: string): Promise<ResolvedLevelMap> => {
  const result = await apiRequestWithAutoRefresh(
    `/level-maps/admin/maps/for-user/${encodeURIComponent(userId)}`,
    { method: 'GET' }
  )
  return result.data
}

/** 建立部門專屬地圖（帶 department）或共用範本（不帶 department，name 必填） */
export const createLevelMap = async (data: {
  department?: string
  name?: string
  description?: string
  /** 省略時複製預設地圖的內容當起點 */
  copyFromMapId?: string
  /**
   * true = 建立不含任何級數的空殼地圖，所有級數回退到預設地圖。
   * 「只想覆寫某一級」時必須用這個，否則該部門會整個脫離預設地圖。
   */
  empty?: boolean
}): Promise<{ id: string }> => {
  const result = await apiRequestWithAutoRefresh('/level-maps/admin/maps', {
    method: 'POST',
    body: JSON.stringify(data)
  })
  return result.data
}

export const updateLevelMap = async (
  id: string,
  data: { name?: string; description?: string | null; isActive?: boolean }
): Promise<void> => {
  await apiRequestWithAutoRefresh(`/level-maps/admin/maps/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

export const updateLevelTier = async (
  mapId: string,
  level: number,
  data: LevelTierInput
): Promise<LevelTier> => {
  const result = await apiRequestWithAutoRefresh(
    `/level-maps/admin/maps/${mapId}/tiers/${level}`,
    { method: 'PUT', body: JSON.stringify(data) }
  )
  return result.data
}

/** 移除部門覆寫，該級改為沿用預設地圖 */
export const resetLevelTier = async (mapId: string, level: number): Promise<void> => {
  await apiRequestWithAutoRefresh(`/level-maps/admin/maps/${mapId}/tiers/${level}`, {
    method: 'DELETE'
  })
}

/** 刪除部門專屬地圖或共用範本；仍有夥伴被個人指定時後端會擋下 */
export const deleteLevelMap = async (id: string): Promise<void> => {
  await apiRequestWithAutoRefresh(`/level-maps/admin/maps/${id}`, { method: 'DELETE' })
}

/** 首次部署用：確保預設地圖存在 */
export const ensureDefaultLevelMap = async (): Promise<{ id: string }> => {
  const result = await apiRequestWithAutoRefresh('/level-maps/admin/ensure-default', {
    method: 'POST'
  })
  return result.data
}
